/**
 * NoteEdit Component
 *
 * @component
 * @description This component displays an interface for editing a note's content.
 * It provides a text input field for editing the note's content, along with "Cancel" and "Save" buttons.
 *
 * @param {NoteEditProps} props - The properties and event handlers required by the component.
 * @returns {JSX.Element} - A JSX element representing the NoteEdit component.
 */
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { TextField } from '@mui/material';

interface NoteEditProps {
  noteContent: string;
  isDisabled?: boolean;
  handleOnChangeContentNote: (event: ChangeEvent<HTMLInputElement>) => void;
}

const NoteEdit = (props: NoteEditProps) => {
  const { noteContent, handleOnChangeContentNote, isDisabled } = props;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [hasMoved, setHasMoved] = useState(false);
  // this is used to prevent the cursor from jumping to the beginning of the input field when the content changes;

  useEffect(() => {
    const focusInput = () => {
      if (inputRef.current && !hasMoved) {
        setHasMoved(true);
        const { length } = noteContent;
        inputRef.current.focus();
        inputRef.current.setSelectionRange(length, length);
        inputRef.current.style.height = '10rem !important';
      }
    };

    // Delay to ensure component is fully rendered (especially for tabs). 100 is an arbitrary value.
    const timeout = setTimeout(focusInput, 100);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteContent]);

  return (
    <div
      style={{
        alignItems: 'flex-start',
        display: 'flex',
        height: '100% !important',
        width: '100%',
      }}>
      <TextField
        disabled={isDisabled}
        id="note-content"
        style={{
          height: '100%',
          flex: 1,
          flexGrow: 1,
          border: 'none',
          alignSelf: 'flex-start',
          justifyContent: 'flex-start',
          textAlign: 'start',
        }}
        multiline
        placeholder="Type your note here..."
        InputProps={{
          style: {
            width: '100%',
            height: '100% !important',
          },
          content: noteContent,
          disableUnderline: true,
          inputRef,
        }}
        variant="standard"
        value={noteContent}
        onChange={handleOnChangeContentNote}
        autoFocus
        inputRef={inputRef}
      />
    </div>
  );
};

export default NoteEdit;
